import { Component, OnDestroy, OnInit } from '@angular/core';
import { Car } from '../../models/car-model';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getCar, removeCar, setCarSold } from '../../store/actions/car.actions';
import { AdCategories } from '../../models/ad-categories.model';
import { filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import * as CategoriesSelectors from './../../store/selectors/categories.selectors';
import * as CarSelectors from '../../store/selectors/car.selectors';
import * as CategoriesActions from './../../store/actions/categories.actions';
import { SafeResourceUrl } from '@angular/platform-browser';
import { selectCarState } from '../../store/selectors/car.selectors';
import { Location } from '@angular/common';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import { Statuses } from '../../shared/constants/status.constants';
import { Plans } from '../../shared/constants/plan.constants';
import { selectIsLoggedIn } from '../../store/selectors/auth.selectors';
declare var bootstrap: any;
import * as L from 'leaflet';

@Component({
  selector: 'app-car-details',
  standalone: false,
  templateUrl: './car-details.component.html',
  styleUrl: './car-details.component.scss',
})
export class CarDetailsComponent implements OnInit, OnDestroy {
  car?: Car;
  loading = false;
  deleting = false;

  car$!: Observable<Car | undefined>;
  categories$!: Observable<AdCategories | null>;
  loadingCategories$!: Observable<boolean>;

  carColorCode: string | undefined = undefined;
  carInteriorColorCode: string | undefined = undefined;
  carCategories: AdCategories | null = null;

  safeMapUrl!: SafeResourceUrl;

  currentImage = '';
  currentImageIndex = 0;
  modalInstance: any;

  copied = false;

  plans = Plans;
  statuses = Statuses;

  deleteReasons = [
    'CAR_DELETE_OPTION_SOLD',
    'CAR_DELETE_OPTION_NO_MORE_SELL',
    'DELETE_OPTION_OTHER',
  ];

  customIcon = L.icon({
    iconUrl: 'icons/car.png',
    iconSize: [18, 18],
    iconAnchor: [10, 7],
  });

  selectedReason: string | null = null;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  isUserLoggedIn$ = this.store.select(selectIsLoggedIn);

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private location: Location
  ) {
    this.car$ = this.store.select(CarSelectors.selectCar);
    this.categories$ = this.store.select(CategoriesSelectors.selectCategories);
    this.loadingCategories$ = this.store.select(CategoriesSelectors.selectCategoriesLoading);
    this.store.dispatch(CategoriesActions.loadCategories());

    this.categories$
      .pipe(
        takeUntil(this.destroy$),
        filter((categories) => !!categories?.colors),
        tap((categories) => {
          this.carCategories = categories;
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
      const carId = Number(paramMap.get('id'));
      this.fetchCar(carId);
    });
  }

  fetchCar(id: number): void {
    this.loading = true;

    this.store.dispatch(getCar({ carId: id }));
    this.car$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (car) => {
        this.car = car;
        this.carColorCode =
          this.carCategories?.colors.find((l) => l.code === this.car?.color)?.description ?? '';
        this.carInteriorColorCode =
          this.carCategories?.colors.find((l) => l.code === this.car?.interiorColor)?.description ??
          '';
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load car', err);
        this.loading = false;
      },
    });
  }

  getCarImageUrl(model: string): string {
    return encodeURI(model.toLowerCase());
  }

  editCar() {
    this.router.navigate(['/add-car/', this.car?.id]);
  }

  goBack() {
    this.location.back();
  }

  deleteCar() {
    if (this.car?.id) {
      this.deleting = true;
      if (this.car.status === Statuses.Sold) {
        this.store.dispatch(removeCar({ id: this.car?.id }));
      }
      if (!this.selectedReason) {
        return;
      }

      if (this.selectedReason === 'CAR_DELETE_OPTION_SOLD') {
        this.store.dispatch(setCarSold({ carId: this.car?.id }));
      } else {
        this.store.dispatch(removeCar({ id: this.car?.id }));
      }

      this.store
        .select(selectCarState)
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ isSaving, error }) => {
          if (!isSaving && !error) {
            this.car = undefined;
            this.router.navigate(['./']);
            this.deleting = false;
            this.closeDeleteModal();
          } else if (!isSaving && error) {
            this.deleting = false;
          }
        });
    }
  }

  closeModal() {
    const modalEl = document.getElementById('imageModal');
    if (modalEl) {
      const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
      modal.hide();
    }
  }

  openDeleteModal() {
    const modalEl = document.getElementById('deleteConfirmModal');
    const modal = new bootstrap.Modal(modalEl!);
    modal.show();
  }

  closeDeleteModal() {
    const modalEl = document.getElementById('deleteConfirmModal');
    if (modalEl) {
      const modal = bootstrap.Modal.getInstance(modalEl) || new bootstrap.Modal(modalEl);
      modal.hide();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
